/**
 * URL synchronization utilities for map location sharing
 * Allows syncing map ID, viewport position (x, y), and zoom level to URL params
 */

/**
 * Parsed URL parameters for map navigation
 */
export interface UrlParams {
  mapId: string | null;
  x: number | null;
  y: number | null;
  zoom: number | null;
}

/**
 * Parses URL search parameters to extract map navigation data
 * @returns Parsed URL parameters with map ID and viewport info
 */
export function parseUrlParams(): UrlParams {
  const params = new URLSearchParams(window.location.search);

  const mapId = params.get('map');
  const xParam = params.get('x');
  const yParam = params.get('y');
  const zoomParam = params.get('z');

  return {
    mapId,
    x: xParam !== null ? parseFloat(xParam) : null,
    y: yParam !== null ? parseFloat(yParam) : null,
    zoom: zoomParam !== null ? parseFloat(zoomParam) : null
  };
}

/**
 * Updates the URL with current map and viewport state without page reload
 * Uses history.replaceState to avoid polluting browser history
 * @param mapId - Current map identifier
 * @param x - Viewport X position in world coordinates
 * @param y - Viewport Y position in world coordinates
 * @param zoom - Viewport zoom level (screen pixels per world unit)
 */
export function updateUrl(mapId: string, x: number, y: number, zoom: number): void {
  const params = new URLSearchParams();

  params.set('map', mapId);
  // Round coordinates to avoid excessively long URLs
  params.set('x', Math.round(x).toString());
  params.set('y', Math.round(y).toString());
  // Round zoom to 6 decimal places for precision without excessive length
  params.set('z', parseFloat(zoom.toPrecision(6)).toString());

  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState(null, '', newUrl);
}

/**
 * Clears map-related URL parameters
 * Used when no map is selected
 */
export function clearUrlParams(): void {
  window.history.replaceState(null, '', window.location.pathname);
}

/**
 * Creates a debounced version of updateUrl to prevent excessive URL updates
 * during rapid viewport changes (panning/zooming)
 * @param delay - Debounce delay in milliseconds
 * @returns Debounced update function
 */
export function createDebouncedUrlUpdate(delay: number = 300): (
  mapId: string,
  x: number,
  y: number,
  zoom: number
) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (mapId: string, x: number, y: number, zoom: number): void => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      updateUrl(mapId, x, y, zoom);
      timeoutId = null;
    }, delay);
  };
}
